import { inject, Injectable, InjectionToken } from '@angular/core';

import { MockHuntingDeviceGate } from '../../../mocks/hunting-device/mock-hunting-device-gate.service';
import { MockHuntingMediaTransport } from '../../../mocks/hunting-device/mock-hunting-media-transport';

import type { MediaFileEntry, MediaTransport, MediaTransferProgress } from './media-transport.port';

// Separate token for the real transport so the facade can hold a real instance while the app-wide
// MEDIA_TRANSPORT token resolves to the facade itself (see app.config.ts).
export const REAL_MEDIA_TRANSPORT = new InjectionToken<MediaTransport>('REAL_MEDIA_TRANSPORT');

/**
 * Routes every transport call to the real WebDAV adapter or the mock based on
 * MockHuntingDeviceGate, so the dev toggle swaps them at runtime instead of build time.
 */
// Delegating wrapper: TypeScript accepts a delegate with fewer parameters, so every method must
// forward the port's full argument list by hand.
@Injectable()
export class MediaTransportFacade implements MediaTransport {
  // services
  private readonly real = inject(REAL_MEDIA_TRANSPORT);
  private readonly mock = inject(MockHuntingMediaTransport);
  private readonly gate = inject(MockHuntingDeviceGate);

  public list$(remoteDir: string): Promise<Array<MediaFileEntry>> {
    return this.gate.isActive ? this.mock.list$(remoteDir) : this.real.list$(remoteDir);
  }

  public size$(remotePath: string): Promise<number> {
    return this.gate.isActive ? this.mock.size$(remotePath) : this.real.size$(remotePath);
  }

  public downloadToFile$(remotePath: string, localPath: string, onProgress?: (progress: MediaTransferProgress) => void): Promise<void> {
    return this.gate.isActive ? this.mock.downloadToFile$(remotePath, localPath, onProgress) : this.real.downloadToFile$(remotePath, localPath, onProgress);
  }

  public isReachable$(): Promise<boolean> {
    return this.gate.isActive ? this.mock.isReachable$() : this.real.isReachable$();
  }
}
